import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StatusToggle } from '../components/agents/StatusToggle';
import { useAgent, useDeleteAgent, useUpdateAgent } from '../hooks/useAgents';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { AgentOverviewTab } from '../components/agents/AgentOverviewTab';
import { AgentPromptsTab } from '../components/agents/AgentPromptsTab';
import { AgentPromptSelector } from '../components/agents/AgentPromptSelector';
import { AgentMcpConnectionSelector } from '../components/agents/AgentMcpConnectionSelector';
import { AgentKnowledgeBaseSelector } from '../components/agents/AgentKnowledgeBaseSelector';
import { useToast } from '../contexts/ToastContext';
import { ResourceVersionControls } from '../components/versioning/ResourceVersionControls';
import { asApiRefusal, refusalReason } from '../utils/apiRefusal';
import type { Agent, UpdateAgentInput } from '../types';

/**
 * The keys Save may send (task 1109), each with how "changed" is decided.
 *
 * Edit copies the whole loaded agent into the working copy, so Save used to
 * PATCH all of it — the nested `modelConfig`, timestamps, and the stored input
 * and output schemas — and the service re-validated a schema the User never
 * touched. Save now sends only the writable keys whose value differs from the
 * loaded agent; everything else (`id`, `accountId`, `modelConfig`, audit
 * fields, populated relations) is never sent.
 */
const DEEP_KEYS = ['input', 'output', 'tools', 'metadata'] as const;
const SCALAR_KEYS = ['name', 'description', 'isActive', 'modelConfigId', 'failoverModelConfigId'] as const;

/** Structural equality for the JSON values in DEEP_KEYS (same rule as ConfigForm). */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(right, key) && deepEqual(left[key], right[key])
  );
};

/** The PATCH body: only the writable keys the User changed. */
const changedFields = (loaded: Agent, edited: Partial<Agent>): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  for (const key of DEEP_KEYS) {
    if (key in edited && !deepEqual(edited[key], loaded[key])) body[key] = edited[key];
  }
  for (const key of SCALAR_KEYS) {
    if (key in edited && edited[key] !== loaded[key]) body[key] = edited[key];
  }
  return body;
};

/**
 * The toast detail for a refused update: the service's own reason, plus the
 * first validation error's field so the User knows WHERE it failed. The reason
 * already IS the first field message when one exists, so the field is prefixed
 * rather than the message repeated.
 */
const updateFailureDetail = (error: unknown): string => {
  const reason = refusalReason(error);
  const first = asApiRefusal(error).validationErrors?.[0];
  if (!first?.field) return reason;
  const message = first.message?.trim();
  if (!message || message === reason) return `${first.field}: ${reason}`;
  return `${reason} (${first.field}: ${message})`;
};

export default function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading, error } = useAgent(id);
  const updateAgent = useUpdateAgent();
  // Delete beside Edit (task 864). This page had none, so an agent could only
  // be deleted from the Agents list — and the dialog here is the one that gets
  // to say "no" when a live AI Service runs on this agent, which is exactly the
  // decision a User standing on the agent's own page is making.
  const deleteAgent = useDeleteAgent();
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isPromptSelectorOpen, setIsPromptSelectorOpen] = useState(false);
  const [isMcpSelectorOpen, setIsMcpSelectorOpen] = useState(false);
  const [isKnowledgeSelectorOpen, setIsKnowledgeSelectorOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedAgent, setEditedAgent] = useState<Partial<Agent>>({});

  // Reset edited agent when agent data changes or editing mode changes
  useEffect(() => {
    if (agent && isEditing) {
      setEditedAgent(agent);
    }
  }, [agent, isEditing]);

  const handleFieldChange = (field: string, value: any) => {
    setEditedAgent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!agent) return;
    const data = changedFields(agent, editedAgent);
    // Nothing changed: leave Edit without a request, and without claiming an
    // update that did not happen.
    if (Object.keys(data).length === 0) {
      setIsEditing(false);
      setEditedAgent({});
      return;
    }
    try {
      // Cast: the writable keys are UpdateAgentInput's, but a cleared
      // description or Model Config is sent as `null`, which the type omits.
      await updateAgent.mutateAsync({ id: id!, data: data as UpdateAgentInput });
      setIsEditing(false);
      toast.success('Agent updated successfully');
    } catch (error) {
      toast.error('Failed to update agent', updateFailureDetail(error));
      console.error('Failed to update agent:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedAgent({});
  };

  const handleStartEditName = () => {
    setEditedName(agent?.name || '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === agent?.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await updateAgent.mutateAsync({ id: id!, data: { name: editedName.trim() } });
      setIsEditingName(false);
    } catch (error) {
      toast.error('Failed to update agent', updateFailureDetail(error));
      console.error('Failed to update name:', error);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  const handleToggleStatus = async () => {
    try {
      await updateAgent.mutateAsync({
        id: id!,
        data: { isActive: !agent?.isActive }
      });
    } catch (error) {
      toast.error('Failed to update agent', updateFailureDetail(error));
      console.error('Failed to toggle status:', error);
    }
  };

  // Early returns for loading/error states must come after all hooks
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading agent...</div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading agent. Please try again.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Custom Header with Inline Name Edit */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                autoFocus
                className="text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-indigo-500 focus:outline-none focus:border-indigo-600 px-1"
              />
              <button
                type="button"
                onClick={handleSaveName}
                disabled={updateAgent.isPending}
                className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                title="Save"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleCancelEditName}
                disabled={updateAgent.isPending}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Cancel"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {agent.name}
              </h1>
              {!isEditing && (
                <>
                  <button
                    type="button"
                    onClick={handleStartEditName}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Edit name"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <StatusToggle
                    isActive={agent.isActive}
                    onToggle={handleToggleStatus}
                    disabled={updateAgent.isPending}
                  />
                </>
              )}
            </div>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Agent #{agent.id} · Created {new Date(agent.createdAt).toLocaleDateString()} · Updated {new Date(agent.updatedAt).toLocaleDateString()}
          </p>
          {/* Version status + Publish (task 725) — read-only indicator, kept out
              of the action column on the right. */}
          <ResourceVersionControls
            entityType="agent"
            entityId={agent.id}
            resourceName={agent.name}
            className="mt-3"
          />
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          {isEditing ? (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={updateAgent.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckIcon className="-ml-1 mr-2 h-4 w-4" />
                {updateAgent.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={updateAgent.isPending}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <XMarkIcon className="-ml-1 mr-2 h-4 w-4" />
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                Edit
              </button>
              <DeleteResourceControl
                flag="innovate.fe.agents.delete"
                entity="agents"
                entityLabel="agent"
                name={agent.name}
                id={agent.id}
                variant="button"
                body={
                  <p>
                    The prompts, MCP connections and knowledge bases attached to it stay in your
                    library.
                  </p>
                }
                onDelete={() => deleteAgent.mutateAsync(agent.id)}
                onDeleted={() => navigate('/agents')}
              />
            </div>
          )}
        </div>
      </div>

      {/* Description - inline below header */}
      <div className="mt-4">
        {isEditing ? (
          <textarea
            value={editedAgent.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Enter agent description"
          />
        ) : agent.description ? (
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {agent.description}
          </p>
        ) : (
          <p className="text-gray-400 dark:text-gray-500 italic">No description</p>
        )}
      </div>

      <Tab.Group>
        <Tab.List className="mt-6 flex space-x-1 border-b border-gray-200 dark:border-gray-700">
          <Tab
            className={({ selected }) =>
              `px-4 py-2 text-sm font-medium focus:outline-none ${
                selected
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`
            }
            disabled={isEditing}
          >
            Overview
          </Tab>
          <Tab
            className={({ selected }) =>
              `px-4 py-2 text-sm font-medium focus:outline-none ${
                selected
                  ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`
            }
            disabled={isEditing}
          >
            Prompts
          </Tab>
        </Tab.List>
        <Tab.Panels className="mt-6">
          <Tab.Panel>
            <AgentOverviewTab
              agent={isEditing ? { ...agent, ...editedAgent } : agent}
              onChangePrompt={() => setIsPromptSelectorOpen(true)}
              isEditing={isEditing}
              onFieldChange={handleFieldChange}
              onAddMcp={() => setIsMcpSelectorOpen(true)}
              onAttachKnowledgeBase={() => setIsKnowledgeSelectorOpen(true)}
            />
          </Tab.Panel>
          <Tab.Panel>
            <AgentPromptsTab
              agentId={agent.id}
              onAssignPrompt={() => setIsPromptSelectorOpen(true)}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Prompt Selector Modal */}
      <AgentPromptSelector
        agentId={id!}
        isOpen={isPromptSelectorOpen}
        onClose={() => setIsPromptSelectorOpen(false)}
      />

      {/* MCP Connection Selector Modal */}
      <AgentMcpConnectionSelector
        agentId={id!}
        isOpen={isMcpSelectorOpen}
        onClose={() => setIsMcpSelectorOpen(false)}
      />

      {/* Knowledge Base Attachment Modal (task 574) */}
      <AgentKnowledgeBaseSelector
        agentId={id!}
        isOpen={isKnowledgeSelectorOpen}
        onClose={() => setIsKnowledgeSelectorOpen(false)}
      />
    </div>
  );
}
